/**
 * Tour entity models — ES module version of MyScripts/Entities/Tour.ts + TourStop.ts
 *
 * These interfaces mirror the Kerkton namespace classes used for
 * Guard Tours and Tour Management.
 */

import type { AttachmentData, AttachmentInfoData } from '@/services/api-types';

// ── Tour Management ──────────────────────────────────────────────────

export interface TourData {
  id: number | null;
  tourIdToCopyTourStops: number | null;
  timeZoneId: string;
  title: string | null;
  type: number | null;
  isEnabled: boolean;
  tourAvailabilityList: TourAvailabilityData[];
  MinStartTime: string | null;
  MaxEndTime: string | null;
}

export interface TourAvailabilityData {
  startTime: number | null;
  endTime: number | null;
  endDayOfWeek: number | null;
  startDayOfWeek: number | null;
  id: number | null;
  guid: string;
}

// ── Tour Stop Management ─────────────────────────────────────────────

export interface TourStopData {
  id: number | null;
  title: string | null;
  client: TourClientData;
  location: string | null;
  locationLatitude: number | null;
  locationLongitude: number | null;
  customLocationId: boolean | null;
  instructions: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  pauseWindowStartDateTime: string | null;
  pauseWindowEndDateTime: string | null;
  isEnabled: boolean;
  isChecklistEnabled: boolean;
  areIssuesEnabled: boolean;
  areOfficerCommentsRequired: number;
  isGeofenceEnabled: boolean;
  timeZoneId: string;
  areOfficerAttachmentsEnabled: boolean;
  enabledTourStopDaysType: number;
  isEnabledOnSaturdays: boolean;
  isEnabledOnSundays: boolean;
  tourStopChecklist: TourStopCheckData[];
  tourStopTaskList: TourStopTaskData[];
  geofence: Array<{ lat: number; lng: number }> | null;
  numTimesToComplete: number | null;
  reminderEmail: string | null;
  lastReminderSentAt: string | null;
}

export interface TourClientData {
  id: number | null;
  name: string | null;
  allSites: any[] | null;
  sites: number[];
}

export interface TourStopCheckData {
  type: string | null;
  instructions: string | null;
  listIdx: number | null;
}

export interface TourStopTaskData {
  tourStopTaskId: number | null;
  taskId: number | null;
  name: string | null;
  description: string | null;
  listIdx: number | null;
  isChecked: boolean | null;
  completedAt: string | null;
  tourExemption: TourExemptionData[];
  timeSensitiveList: SensitiveTaskData[];
  timeSensitive: SensitiveTaskData | null;
  numAssociatedTourStops: number | null;
  uniqueId: string;
  exceptionId: number | null;
}

export interface TourExemptionData {
  name: string | null;
  description: string | null;
}

export interface SensitiveTaskData {
  startTime: number | null;
  endTime: number | null;
  startDayOfWeek: number | null;
  endDayOfWeek: number | null;
  guid: string;
  endDateTime: string | null;
  startDateTime: string | null;
}

// ── Guard Tours (runtime instances) ──────────────────────────────────

export interface GuardTourData {
  id: number | null;
  /** The Tour revision this occurrence was materialized from. */
  tourId: number | null;
  /** Stable across tour edits; joins schedule data even when tourId is a superseded revision. */
  tourRevisionGroup: number | null;
  title: string | null;
  clientName: string | null;
  startTimeLocal: number | null;
  endTimeLocal: number | null;
  timeZoneLocal: string | null;
  stopsCompleted: number | null;
  totalStops: number | null;
  officerNames: string[];
}

export interface GuardTourStopData {
  id: number | null;
  title: string;
  npsdClientName: string;
  npsdClientSiteNames: string;
  location: string;
  instructions: string;
  tourStopChecklist: TourStopChecklistItemData[];
  tourStopTaskList: GuardTourStopTaskData[];
  tourStopIssues: TourStopIssueData[];
  claimedByIdList: number[];
  areOfficerCommentsRequired: number;
  areOfficerAttachmentsEnabled: boolean;
  newTourStopIssue: TourStopIssueData | null;
  areIssuesEnabled: boolean | null;
  activeStartDateTime: string | null;
  activeEndDateTime: string | null;
  // When the stop is currently inactive but still visible. Enum name from backend:
  // 'PAUSE_WINDOW' | 'CALENDAR_SATURDAY' | 'CALENDAR_SUNDAY' | 'CALENDAR_DAY_OF_WEEK' | 'CALENDAR_EVEN_DAYS' | 'CALENDAR_ODD_DAYS'.
  // Null when the stop is actively completable.
  inactiveReason: string | null;
  // Formatted Instant. Populated only for PAUSE_WINDOW (when the stop will reactivate).
  inactiveUntil: string | null;
  officerComments: string;
  latitude: number | null;
  longitude: number | null;
  numTimesToComplete: number | null;
  completionList: GuardTourCompletionData[];
  resizedImageList: AttachmentData[];
  attachmentList: AttachmentData[];
  newAttachmentInfoList: AttachmentInfoData[];
  geofence: Array<{ lat: number; lng: number }> | null;
  tourStopId: number | null;
}

export interface TourStopChecklistItemData {
  id: number | null;
  type: string;
  instructions: string;
  completedAt: string | null;
}

export interface GuardTourStopTaskData {
  name: string | null;
  description: string | null;
  listIdx: number | null;
  completedAt: string | null;
  tourExemption: TourExemptionData;
  tourStopTaskId: number | null;
  timeSensitive: GuardTourStopTaskTimeSensitiveData;
  exceptionId: number | null;
  isChecked: boolean;
}

export interface GuardTourStopTaskTimeSensitiveData {
  startDateTime: string | null;
  endDateTime: string | null;
}

export interface TourStopIssueData {
  id: number | null;
  title: string | null;
  description: string | null;
  raisedAt: string | null;
  raisedByName: string | null;
  resolvedAt: string | null;
  resolvedByName: string | null;
  isResolved: boolean;
  resizedImageList: any[];
  newAttachmentInfoList: any[];
  tempid: string | null;
}

export interface GuardTourCompletionData {
  id: number | null;
  completedAt: string | null;
  completedByName: string | null;
  latitude: string | null;
  longitude: string | null;
  officerComments: string | null;
}

export interface PendingCaseReportData {
  id: number | null;
  caseId: string | null;
  natureOfCallValues: string[];
  callLoc: string | null;
  author: number | null;
  authorName: string | null;
}

export interface UpdateOfficerCommentsData {
  id: number | null;
  officerComments: string | null;
  guardTourStopId: number | null;
}

// ── Week Day helpers ─────────────────────────────────────────────────

export interface WeekDayData {
  dayOfWeek: number;
  name: string;
}

// ── Enums matching legacy values ─────────────────────────────────────

export const OfficerCommentsRequired = {
  NOT_REQUIRED: 0,
  REQUIRED: 1,
} as const;

export const OfficerAttachmentsEnabled = {
  DISABLED: false,
  ENABLED: true,
} as const;
